// Frontend realtime client wrapper (resilient realtime transport)
import { sanitizeOperationId } from '../shared/operationId.js';
import { playProceduralSound } from '@/components/System/SoundManager';
import { MEDICAL_EVENT_TYPES } from '@/features/medicalServices/realtime/medicalServicesEvents.js';
import { participantDisplayName } from '@/utils/participantDisplay.js';
import { apiClient } from './Client';
import { createEventRecovery } from './eventRecovery.js';

// Constants must be declared first
const FALLBACK_OPERATION_ID = null;
const FEED_LIMIT = 40;
const FEED_TTL_MS = 2500;
const EMIT_TTL_MS = 450;
const OPERATIONAL_ACCESS_EVENT_TYPES = Object.freeze([
    'OPERATIONAL_ACCESS_SCOPE',
    'OPERATIONAL_POSTURE_CHANGED',
    'OPERATIONAL_POSTURE_REQUESTS_CHANGED',
    'OPERATIONAL_POLICY_CHANGED',
]);

// State variables
let socket = null;
let connected = false;
let connectPromise = null;
const listeners = {};
const participants = new Map();
const eventFeed = [];
const recentFeedKeys = new Map();
const recentEmits = new Map();
let degraded = true;
let currentOperationId = null;
let currentReady = false;
try {
    const storedOperationId = typeof localStorage !== 'undefined' ? localStorage.getItem('reacta_operationId') : null;
    currentOperationId = sanitizeOperationId(storedOperationId);
    if (currentOperationId === null) {
        currentOperationId = null;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('reacta_operationId');
        }
    }
    currentReady = false;
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('reacta_readyState');
    }
} catch (error) {
    console.warn('[REALTIME] armazenamento local indisponível', error);
    currentOperationId = null;
    currentReady = false;
}
let reconnecting = false;
let heartbeatTimer = null;
let lastEventAt = null;
let latencyMs = null;
let operationState = null;
let currentIdentity = null; // Armazena a identidade autoritativa da conexão

// Operational sound integration (discrete, non-blocking)
function playOperationalSound(soundType) {
    try {
        playProceduralSound(soundType);
    } catch (error) {
        // Audio must never block realtime transport.
    }
}
const DEFAULT_OPERATION_STATE = Object.freeze({
    operationStatus: 'STANDBY',
    alertLevel: 'GREEN',
    commandMode: 'MONITORING',
    activeSector: 'GENERAL',
    currentObjective: 'Aguardando objetivo operacional',
    operationalPhase: 'ASSESSMENT',
    lastUpdate: null,
});

const NO_OPERATION_STATE = Object.freeze({
    operationId: null,
    operationStatus: 'SEM_OPERACAO',
    alertLevel: null,
    commandMode: null,
    activeSector: null,
    currentObjective: null,
    operationalPhase: null,
    selectedPhenomenonLayer: 'none',
    lastUpdate: null,
    message: 'Operação não definida',
});

function normalizeBaseUrl(value) {
    if (!value) return '';
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    try {
        const parsed = new URL(value, base);
        return parsed.origin || value.replace(/\/+$/, '');
    } catch (error) {
        return value.replace(/\/+$/, '');
    }
}

function resolveRealtimeEndpoint() {
    const socketEnv = import.meta.env.VITE_SOCKET_URL;
    const apiEnv = import.meta.env.VITE_API_URL;
    const candidate = socketEnv || apiEnv;
    if (candidate) {
        const normalized = normalizeBaseUrl(candidate);
        if (normalized) return normalized;
    }
    if (import.meta.env.DEV) {
        return 'http://localhost:3001';
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return 'http://localhost:3001';
}

// A função getIdentity foi removida. A identidade agora é gerenciada internamente.

function getLocalOperator() {
    const sessionId = localStorage.getItem('reacta_sessionId')
        || sessionStorage.getItem('reacta_sessionId')
        || localStorage.getItem('reacta_session_id')
        || sessionStorage.getItem('reacta_session_id')
        || null;

    if (currentIdentity) {
        const operatorId = currentIdentity.operatorId
            || currentIdentity.operator_id
            || currentIdentity.userId
            || currentIdentity.user_id
            || currentIdentity.id
            || currentIdentity.email
            || currentIdentity.name
            || currentIdentity.full_name
            || null;
        const role = currentIdentity.role
            || currentIdentity.roleName
            || currentIdentity.role_name
            || currentIdentity.sessionRole
            || currentIdentity.session_role
            || null;
        return {
            sessionId,
            operatorId,
            role,
        };
    }
    // Retorna um operador "nulo" seguro se nenhuma identidade estiver definida
    return {
        sessionId,
        operatorId: null,
        role: null,
    };
}

function getPayloadOperationId(payload = {}) {
    return sanitizeOperationId(
        payload.operationId
        || payload.incidentId
        || payload.metadata?.operationId
        || payload.metadata?.operation_id
        || payload.data?.operationId
        || payload.data?.operation_id
    );
}

function isSameOperation(payload = {}) {
    const payloadOperationId = getPayloadOperationId(payload);
    const activeOperationId = currentOperationId;
    if (!payloadOperationId && !activeOperationId) return true;
    if (!payloadOperationId) return false;
    if (!activeOperationId) return false;
    return payloadOperationId === activeOperationId;
}

function getParticipantKey(payload = {}) {
    const qualifier = sanitizeOperationId(payload.operationId) || currentOperationId || 'global';
    const baseKey = payload.operatorId || payload.presenceId || payload.socketId || 'guest';
    return `${qualifier}:${baseKey}`;
}

// Nome para o feed: o do evento ou o ja conhecido na Presence; nunca o id
// tecnico de convite.
function participantLabel(payload = {}, operatorId = payload.operatorId) {
    const known = participants.get(getParticipantKey({ operationId: payload.operationId, operatorId })) || {};
    return participantDisplayName({ ...known, ...payload, operatorId }) || 'Operador';
}

function isFallbackOperation(operationId) {
    return !operationId || operationId === FALLBACK_OPERATION_ID;
}

// P2: OPERATIONAL_EVENT que o socket nao entregou (queda, reinicio do
// servidor) e relido do journal ao (re)conectar e entregue aos mesmos
// ouvintes, marcado recovered:true; o ja entregue e descartado
// (ver eventRecovery.js).
const eventRecovery = createEventRecovery({
    fetchPage: ({ operationId, after }) => apiClient.get(
        `/api/events/operational/recovery?operationId=${encodeURIComponent(operationId)}${after === null ? '' : `&after=${after}`}`
    ),
    deliver: (event) => {
        pushFeed(event.type || 'OPERATIONAL_EVENT', formatOperationalEventDetail(event), event);
        emitLocal('OPERATIONAL_EVENT', event);
    },
});

function trackRecoveryOperation() {
    eventRecovery.setOperation(isFallbackOperation(currentOperationId) ? null : currentOperationId);
}

function syncEventRecovery() {
    trackRecoveryOperation();
    if (connected) eventRecovery.sync();
}

function pruneRecent(map, now = Date.now()) {
    Array.from(map.entries()).forEach(([key, expiresAt]) => {
        if (expiresAt <= now) map.delete(key);
    });
}

function pushFeed(type, detail, payload = {}) {
    const payloadOperationId = getPayloadOperationId(payload);
    if (payloadOperationId && payloadOperationId !== currentOperationId) return;
    const now = Date.now();
    pruneRecent(recentFeedKeys, now);
    const dedupeKey = [
        type,
        payload.operationId || currentOperationId,
        payload.operatorId || payload.presenceId || payload.socketId || '',
        payload.ready === undefined ? '' : String(payload.ready),
        detail || ''
    ].join('|');
    if (recentFeedKeys.has(dedupeKey)) return;
    recentFeedKeys.set(dedupeKey, now + FEED_TTL_MS);
    const entry = {
        id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
        type,
        detail,
        operationId: payloadOperationId || currentOperationId,
        timestamp: new Date().toISOString(),
        payload
    };
    lastEventAt = entry.timestamp;
    eventFeed.unshift(entry);
    if (eventFeed.length > FEED_LIMIT) eventFeed.length = FEED_LIMIT;
    emitLocal('OPERATION_FEED', entry);
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
}

function formatOperationalEventDetail(payload = {}) {
    const data = payload.data || {};
    if (payload.type === 'INSTITUTION_REPRESENTATIVE_JOINED') {
        return `${data.agency || payload.metadata?.agency || 'Instituição'}: representante autenticado na operação`;
    }
    if (payload.type === 'TACTICAL_DECISION' && data.orderType === 'WRITTEN_ORDER') {
        const target = data.targetLabel || data.targetRole || 'gestor';
        return `Ordem escrita para ${target}: ${data.decision || 'sem texto'}`;
    }
    if (payload.type === 'TACTICAL_DECISION' && data.decision) {
        return `Decisao tatica: ${data.decision}`;
    }
    return payload.detail || payload.type || 'Evento operacional recebido';
}

function setOperationStateSnapshot(payload = {}) {
    const payloadOperationId = sanitizeOperationId(payload.operationId);
    if (!payloadOperationId) {
        if (currentOperationId) {
            console.warn('[REALTIME] snapshot ignorado: operationId nao definido');
            return;
        }
        operationState = { ...NO_OPERATION_STATE };
        emitLocal('OPERATION_STATE_UPDATED', operationState);
        emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
        return;
    }
    if (currentOperationId && payloadOperationId !== currentOperationId) return;
    if (!currentOperationId) {
        currentOperationId = payloadOperationId;
        emitLocal('OPERATION_CHANGED', getOperationSnapshot());
    }
    operationState = {
        ...DEFAULT_OPERATION_STATE,
        ...operationState,
        ...payload,
        operationId: payloadOperationId,
    };
    emitLocal('OPERATION_STATE_UPDATED', operationState);
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
}

function upsertParticipant(payload = {}) {
    if (!isSameOperation(payload)) return;
    const key = getParticipantKey(payload);
    const existing = participants.get(key) || {};
    participants.set(key, {
        ...existing,
        ...payload,
        operationId: payload.operationId || currentOperationId,
        online: payload.online !== false,
        reconnecting: payload.reconnecting || false,
        degraded: degraded || payload.degraded || false,
        lastActivity: payload.lastActivity || new Date().toISOString()
    });
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
}

function safeEmitEvent(name, payload) {
    const needsOperationContext = name.startsWith('OPERATION_') || name === 'READY_STATE_CHANGED';
    const payloadOperationId = sanitizeOperationId(payload?.operationId);
    const targetOperationId = payloadOperationId || currentOperationId;
    if (needsOperationContext && !targetOperationId) {
        console.warn(`[REALTIME] evento ${name} ignorado: operationId ausente`);
        return;
    }
    const normalizedPayload = payloadOperationId || !needsOperationContext
        ? payload
        : { ...payload, operationId: targetOperationId };
    const now = Date.now();
    pruneRecent(recentEmits, now);
    const dedupeOperationId = needsOperationContext ? targetOperationId : (normalizedPayload?.operationId || targetOperationId || '');
    const key = `${name}|${dedupeOperationId}|${normalizedPayload?.ready ?? ''}`;
    if ((name === 'READY_STATE_CHANGED' || name === 'OPERATION_JOINED') && recentEmits.has(key)) return;
    recentEmits.set(key, now + EMIT_TTL_MS);
    if (socket && connected) socket.emit(name, normalizedPayload);
    else console.debug('[REALTIME][UNAVAILABLE] emit skipped until socket reconnects', name, normalizedPayload);
}

export async function connect({ url, identity } = {}) {
    if (connected || socket) return;
    if (connectPromise) return connectPromise;
    connectPromise = connectInternal({ url, identity }).finally(() => {
        connectPromise = null;
    });
    return connectPromise;
}

export function getActiveOperationId() {
    return currentOperationId;
}

export function subscribeOperationChange(handler, { immediate = true } = {}) {
    if (typeof handler !== 'function') {
        return () => { };
    }
    if (immediate) {
        try {
            handler(currentOperationId, getOperationSnapshot());
        } catch (error) {
            console.warn('[REALTIME] subscribeOperationChange handler failed', error);
        }
    }
    const unsubscribe = on('OPERATION_CHANGED', () => {
        try {
            handler(currentOperationId, getOperationSnapshot());
        } catch (error) {
            console.warn('[REALTIME] subscribeOperationChange handler failed', error);
        }
    });
    return () => {
        try {
            unsubscribe && unsubscribe();
        } catch (error) {
            console.warn('[REALTIME] unsubscribe operation change failed', error);
        }
    };
}

export function verifyOperationConsistency(source, observedOperationId) {
    const active = currentOperationId;
    if (!active && !observedOperationId) return true;
    if (!active && observedOperationId) {
        console.warn('[OPERATION-CONSISTENCY] Active operation not set but component reported value', { source, observedOperationId });
        return false;
    }
    if (active && !observedOperationId) {
        console.warn('[OPERATION-CONSISTENCY] Component missing operationId while realtime has one', { source, activeOperationId: active });
        return false;
    }
    if (active && observedOperationId && active !== observedOperationId) {
        console.warn('[OPERATION-CONSISTENCY] Divergent operationId detected', { source, activeOperationId: active, componentOperationId: observedOperationId });
        return false;
    }
    return true;
}

// P4: operacao do handshake = operacao atual da estacao. O Manager do
// socket.io-client reaproveita o query do primeiro io() em toda reconexao;
// sem isto, quem saiu ou trocou de operacao voltava, pelo servidor, a
// operacao do primeiro connect (presenca fantasma la, sem OPERATION_LEFT).
function withCurrentOperation(query = {}) {
    const next = { ...query };
    if (isFallbackOperation(currentOperationId)) delete next.operationId;
    else next.operationId = currentOperationId;
    return next;
}

async function connectInternal({ url, identity: explicitIdentity } = {}) {
    const apiUrl = url || resolveRealtimeEndpoint();

    // A identidade explícita passada é a única fonte da verdade.
    // Se não for fornecida, a conexão não deve prosseguir.
    if (!explicitIdentity) {
        console.error('[REALTIME] Conexão abortada: Nenhuma identidade fornecida para o handshake.');
        return Promise.resolve();
    }

    currentIdentity = explicitIdentity; // Armazena a identidade autoritativa
    const { sessionId, operatorId, role } = getLocalOperator();

    // ================= AUDITORIA: INÍCIO DA CORREÇÃO =================
    // Trava de segurança para impedir conexão sem identidade válida.
    if (!operatorId || !role || operatorId === 'guest') {
        console.error('[REALTIME] Conexão abortada: identidade inválida ou de convidado. Aguardando autenticação.', { operatorId, role });
        // Retornar uma promessa resolvida para não quebrar a cadeia de chamadas
        return Promise.resolve();
    }
    // ================= AUDITORIA: FIM DA CORREÇÃO ===================

    try {
        const mod = await import('socket.io-client');
        const io = mod.io || mod.default;
        // include operationId in handshake only when explicit to avoid premature room association
        const query = withCurrentOperation({ sessionId, operatorId, role });
        socket = io(apiUrl, { query, autoConnect: true });

        socket.on('connect', () => {
            connected = true; degraded = false; reconnecting = false;
            console.info('[REALTIME] connected', socket.id);
            playOperationalSound('connect');
            if (!isFallbackOperation(currentOperationId)) {
                safeEmitEvent('OPERATION_JOINED', { operationId: currentOperationId });
                safeEmitEvent('OPERATION_STATE_REQUEST', { operationId: currentOperationId });
            }
            pushFeed('REALTIME_CONNECTED', 'Link realtime conectado', { operationId: currentOperationId });
            emitLocal('REALTIME_STATUS', getOperationSnapshot());
            syncEventRecovery();

            clearInterval(heartbeatTimer);
            heartbeatTimer = setInterval(() => {
                if (!socket || !connected) return;
                const startedAt = Date.now();
                socket.timeout(1500).emit('HEARTBEAT', { operationId: currentOperationId, clientTime: startedAt }, () => {
                    latencyMs = Date.now() - startedAt;
                    lastEventAt = new Date().toISOString();
                    emitLocal('REALTIME_STATUS', getOperationSnapshot());
                });
            }, 12000);
        });

        socket.on('disconnect', (reason) => {
            connected = false; degraded = true; reconnecting = reason !== 'io client disconnect';
            clearInterval(heartbeatTimer);
            console.warn('[REALTIME] disconnected', reason);
            playOperationalSound('degraded');
            pushFeed('DEGRADED_MODE', `Realtime degradado: ${reason}`, { operationId: currentOperationId });
            emitLocal('REALTIME_STATUS', getOperationSnapshot());
        });

        socket.io.on('reconnect_attempt', () => {
            reconnecting = true;
            socket.io.opts.query = withCurrentOperation(socket.io.opts.query);
            pushFeed('RECONNECTING', 'Tentando reconectar sessao realtime', { operationId: currentOperationId });
        });

        socket.io.on('reconnect', () => {
            reconnecting = false; degraded = false;
            playOperationalSound('reconnect');
            if (!isFallbackOperation(currentOperationId)) {
                safeEmitEvent('OPERATION_JOINED', { operationId: currentOperationId });
                safeEmitEvent('OPERATION_STATE_REQUEST', { operationId: currentOperationId });
            }
            pushFeed('RECONNECT', 'Sessao realtime restaurada', { operationId: currentOperationId });
        });

        socket.on('USER_CONNECTED', (payload) => {
            if (!isSameOperation(payload)) return;
            upsertParticipant({ ...payload, online: true });
            pushFeed('USER_CONNECTED', `${participantLabel(payload)} entrou`, payload);
            emitLocal('USER_CONNECTED', payload);
        });

        socket.on('PRESENCE_SNAPSHOT', (payload = {}) => {
            if (!isSameOperation(payload)) return;
            
            participants.clear();
            const seen = new Set();
            
            (payload.participants || []).forEach((participant) => {
                const key = getParticipantKey(participant);
                if (seen.has(key)) return;
                seen.add(key);
                participants.set(key, {
                    ...participant,
                    operationId: participant.operationId || payload.operationId || currentOperationId,
                    online: participant.online !== false,
                    reconnecting: Boolean(participant.reconnecting),
                });
            });
            
            emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
            pushFeed('PRESENCE_SNAPSHOT', 'Presenca operacional sincronizada', payload);
        });

        socket.on('OPERATIONAL_EVENT', (payload) => {
            if (!isSameOperation(payload)) return;
            const normalizedPayload = { ...payload, operationId: getPayloadOperationId(payload) || currentOperationId };
            trackRecoveryOperation();
            if (!eventRecovery.accept(normalizedPayload)) return;
            pushFeed(payload.type || 'OPERATIONAL_EVENT', formatOperationalEventDetail(payload), normalizedPayload);
            emitLocal('OPERATIONAL_EVENT', normalizedPayload);
        });

        // Aviso minimo de mudanca da sala (SEC-EVENTBUS-01): so operationId,
        // updatedAt e reason; a sala e relida pela rota autorizada
        // (useActiveRoomIncident). Nunca traz o objeto da sala.
        socket.on('ROOM_UPDATED', (payload) => {
            if (!isSameOperation(payload)) return;
            emitLocal('ROOM_UPDATED', payload);
        });

        socket.on('OPERATION_STATE_SNAPSHOT', (payload) => {
            setOperationStateSnapshot(payload);
            pushFeed('OPERATION_STATE_SNAPSHOT', 'Contexto operacional sincronizado', payload);
        });

        socket.on('ACCOUNT_INVITE_GENERATED', (payload) => {
            pushFeed('ACCOUNT_INVITE_GENERATED', `Convite gerado para ${payload.name || payload.email || 'usuario'}`, { ...payload, operationId: currentOperationId });
        });

        // operation awareness events
        socket.on('OPERATION_JOINED', (payload) => {
            if (!isSameOperation(payload)) return;
            try {
                pushFeed('OPERATION_JOINED', `${payload.operatorId || 'Operador'} entrou na operacao`, payload);
                emitLocal('OPERATION_JOINED', payload);
            } catch (e) { /* ignore */ }
        });

        socket.on('SESSION_RESTORED', (payload) => {
            // server may provide restored operational hints; if it carries an operationId, persist and re-join
            try {
                const restoredOperationId = payload?.operationId || payload?.operationalState?.operationId;
                if (restoredOperationId) {
                    setOperationId(restoredOperationId);
                    // inform server explicitly (best-effort)
                    safeEmitEvent('OPERATION_JOINED', { operationId: restoredOperationId });
                    safeEmitEvent('OPERATION_STATE_REQUEST', { operationId: restoredOperationId });
                }
                pushFeed('SESSION_RESTORED', 'Sessao restaurada', { ...payload, operationId: currentOperationId });
                emitLocal('SESSION_RESTORED', payload);
            } catch (e) { /* ignore */ }
        });

        socket.on('USER_DISCONNECTED', (payload) => {
            if (!isSameOperation(payload)) return;
            const key = getParticipantKey(payload);
            const existing = participants.get(key) || payload;
            participants.set(key, { ...existing, ...payload, online: payload.reconnecting ? true : false, reconnecting: Boolean(payload.reconnecting), lastActivity: new Date().toISOString() });
            pushFeed('USER_DISCONNECTED', `${participantLabel(payload)} ${payload.reconnecting ? 'reconectando' : 'saiu'}`, payload);
            emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
            emitLocal('USER_DISCONNECTED', payload);
        });

        socket.on('PRESENCE_OFFLINE', (payload = {}) => {
            if (!isSameOperation(payload)) return;
            const key = getParticipantKey(payload);
            const existing = participants.get(key) || payload;
            participants.set(key, {
                ...existing,
                ...payload,
                online: false,
                reconnecting: false,
                lastActivity: new Date().toISOString(),
            });
            pushFeed('PRESENCE_OFFLINE', `${payload.operatorId || 'Operador'} offline`, payload);
            emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
            emitLocal('PRESENCE_OFFLINE', payload);
        });

        socket.on('COMMS_MESSAGE_CREATED', (payload = {}) => {
            if (!isSameOperation(payload)) return;
            emitLocal('COMMS_MESSAGE_CREATED', payload);
        });

        // SEC-ROOM-FREEZE-01: encerrada, a operacao continua carregada para
        // consulta (mapa, zonas, endereco, registros); so passa a CLOSED.
        socket.on('OPERATION_CLOSED', (payload = {}) => {
            if (!isSameOperation(payload)) return;
            operationState = { ...operationState, operationStatus: 'CLOSED' };
            pushFeed('OPERATION_CLOSED', 'Ocorrencia encerrada — sala congelada para consulta', payload);
            emitLocal('OPERATION_CLOSED', payload);
            emitLocal('OPERATION_STATE_UPDATED', operationState);
        });

        socket.on('READY_STATE_CHANGED', (payload) => {
            if (!isSameOperation(payload)) return;
            upsertParticipant(payload);
            pushFeed('READY_STATE_CHANGED', `${participantLabel(payload)} READY ${payload.ready ? 'YES' : 'NO'}`, payload);
            emitLocal('READY_STATE_CHANGED', payload);
        });

        socket.on('USER_ADMITTED', (payload) => {
            if (!isSameOperation(payload)) return;
            const key = getParticipantKey(payload);
            const existing = participants.get(key);
            if (existing) {
                participants.set(key, { ...existing, admitted: true, admissionStatus: 'admitted', admittedBy: payload.admittedBy, admittedAt: payload.timestamp });
            }
            pushFeed('USER_ADMITTED', `${participantLabel(payload)} foi admitido na operacao`, payload);
            emitLocal('USER_ADMITTED', payload);
        });

        socket.on('ROLE_ASSIGNED', (payload = {}) => {
            if (!isSameOperation(payload)) return;
            const key = getParticipantKey({ operationId: payload.operationId, operatorId: payload.targetOperatorId });
            const existing = participants.get(key);
            if (existing) {
                participants.set(key, { ...existing, role: payload.newRole, operationalRole: payload.newRole });
            }
            pushFeed('ROLE_ASSIGNED', `${participantLabel({ operationId: payload.operationId }, payload.targetOperatorId)} recebeu papel ${payload.newRole}`, payload);
            emitLocal('ROLE_ASSIGNED', payload);
        });

        // Sprint B: negativa de autoridade emitida pelo backend em canais
        // realtime (ex.: OPERATION_STATE_UPDATE rejeitado, socketServer.js)
        // repassada como evento local generico — a UI (AccessDenied.jsx)
        // decide como apresentar, este modulo permanece livre de import de
        // componente.
        socket.on('ACCESS_DENIED', (payload = {}) => {
            emitLocal('ACCESS_DENIED', payload);
        });

        // Restauracao do Oficial de Inteligencia: notificacao discreta de
        // novo Relatorio Reservado, sem conteudo (so reportId + papeis
        // destinatarios) — a UI decide quem exibe o indicador, este modulo
        // apenas repassa o evento local generico.
        socket.on('INTELLIGENCE_REPORT_NOTIFICATION', (payload = {}) => {
            emitLocal('INTELLIGENCE_REPORT_NOTIFICATION', payload);
        });

        // Bridge dos eventos MEDICAL_* (Fase 2 UI, Parte 2, Secao 17): o
        // backend emite cada evento com seu proprio nome via
        // eventBus.broadcastToOperation (io.to(room).emit(eventName, ...)),
        // entao sem um socket.on(nome, ...) explicito por evento aqui o
        // frontend nunca recebe nada — mesma limitacao que ja existe para
        // MASTER_PLAN_REVISION_PUBLISHED. Loop escopado (nao onAny()):
        // registra so os 19 nomes reais do dominio medico, cada um
        // filtrado por isSameOperation antes de repassar (evento de outra
        // operacao nunca atualiza a operacao atual).
        MEDICAL_EVENT_TYPES.forEach((eventName) => {
            socket.on(eventName, (payload = {}) => {
                if (!isSameOperation(payload)) return;
                emitLocal(eventName, payload);
            });
        });

        // Modo Tatico F5: avisos de postura (F1), politica (F3) e escopo de
        // acesso (F4). Nenhum traz conteudo: so revisao, modo ou escopo. A UI
        // rele a fonte canonica (GET operational-posture/operational-policy).
        // Escopo "restricted" limpa aqui o que este modulo guarda da operacao
        // (presenca, estado, feed); "full" nao devolve nada sozinho: a UI so
        // reabre depois de confirmar pela rota.
        OPERATIONAL_ACCESS_EVENT_TYPES.forEach((eventName) => {
            socket.on(eventName, (payload = {}) => {
                if (!isSameOperation(payload)) return;
                if (eventName === 'OPERATIONAL_ACCESS_SCOPE' && payload.scope === 'restricted') {
                    purgeOperationData(payload.operationId || currentOperationId);
                }
                emitLocal(eventName, payload);
            });
        });

        degraded = false;
    } catch (e) {
        console.debug('[REALTIME] socket.io-client not available; realtime transport unavailable', e?.message || e);
        socket = null; connected = false; degraded = true;
        pushFeed('REALTIME_UNAVAILABLE', 'Realtime indisponivel; presenca canonica aguardando backend', { operationId: currentOperationId });
        emitLocal('REALTIME_STATUS', getOperationSnapshot());
    }
}

export function on(eventName, handler) {
    listeners[eventName] = listeners[eventName] || new Set();
    listeners[eventName].add(handler);
    return () => {
        listeners[eventName]?.delete(handler);
    };
}

function emitLocal(eventName, payload) {
    (listeners[eventName] || new Set()).forEach(h => { try { h(payload); } catch (e) { console.error('[REALTIME] listener error', e); } });
}

export function sendReady(operationId, ready) {
    const op = sanitizeOperationId(operationId || currentOperationId);
    currentReady = Boolean(ready);
    if (!op) {
        console.warn('[REALTIME] READY_STATE_CHANGED ignorado: operationId ausente');
        emitLocal('REALTIME_STATUS', getOperationSnapshot());
        return;
    }
    safeEmitEvent('READY_STATE_CHANGED', { operationId: op, ready: currentReady });
    const { sessionId, operatorId, role } = getLocalOperator();
    pushFeed('READY_STATE_CHANGE_REQUESTED', `${operatorId} solicitou READY ${currentReady ? 'YES' : 'NO'}`, { operatorId, operationId: op, ready: currentReady });
}

export function joinOperation(operationId) {
    const nextOperationId = sanitizeOperationId(operationId);
    if (!nextOperationId) {
        console.warn('[REALTIME] joinOperation ignorado: operationId invalido');
        return;
    }
    if (nextOperationId && nextOperationId === currentOperationId) {
        safeEmitEvent('OPERATION_JOINED', { operationId: currentOperationId });
        return;
    }
    participants.clear();
    operationState = null;
    setOperationId(nextOperationId);
    
    const { sessionId, operatorId, role } = getLocalOperator();
    
    // Log para diagnóstico
    console.log('[REALTIME] joinOperation: Identidade final:', { operatorId, role });
    
    if (!currentOperationId) {
        console.warn('[REALTIME] joinOperation sem operationId: estado local resetado');
        emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
        return;
    }
    safeEmitEvent('OPERATION_JOINED', { operationId: currentOperationId });
    safeEmitEvent('OPERATION_STATE_REQUEST', { operationId: currentOperationId });
    syncEventRecovery();
    pushFeed('OPERATION_ACTIVATED', `Operacao ${currentOperationId} ativada`, { operationId: currentOperationId, operatorId });
}

export function leaveOperation() {
    const previousOperationId = currentOperationId;
    if (previousOperationId) {
        safeEmitEvent('OPERATION_LEFT', { operationId: previousOperationId });
    }

    currentReady = false;
    participants.clear();
    operationState = { ...NO_OPERATION_STATE };
    try {
        localStorage.removeItem('reacta_readyState');
        localStorage.removeItem('reacta_inviteMode');
        localStorage.removeItem('reacta_roomName');
        localStorage.removeItem('reacta_institution');
    } catch (error) {
        console.warn('[REALTIME] nao foi possivel limpar estado local da operacao', error);
    }
    setOperationId(null);
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
    emitLocal('OPERATION_LEFT', { operationId: previousOperationId });
}

export function setOperationId(operationId) {
    const sanitized = sanitizeOperationId(operationId);
    if (!sanitized) {
        currentOperationId = null;
        operationState = { ...NO_OPERATION_STATE };
        emitLocal('OPERATION_STATE_UPDATED', operationState);
        emitLocal('OPERATION_CHANGED', getOperationSnapshot());
        try {
            localStorage.removeItem('reacta_operationId');
        } catch (error) {
            console.warn('[REALTIME] nao foi possivel limpar operationId', error);
        }
        return currentOperationId;
    }

    if (sanitized === null && currentOperationId !== null) {
        console.warn('[REALTIME] setOperationId ignorado: fallback nao pode substituir operacao ativa', {
            activeOperationId: currentOperationId,
        });
        return currentOperationId;
    }

    currentOperationId = sanitized === null ? null : sanitized;
    try {
        if (currentOperationId) localStorage.setItem('reacta_operationId', currentOperationId);
        else localStorage.removeItem('reacta_operationId');
    } catch (error) {
        console.warn('[REALTIME] nao foi possivel persistir operationId', error);
    }

    if (!currentOperationId) {
        operationState = { ...NO_OPERATION_STATE };
        emitLocal('OPERATION_STATE_UPDATED', operationState);
    }

    emitLocal('OPERATION_CHANGED', getOperationSnapshot());
    return currentOperationId;
}

// Modo Tatico F5: acesso restringido pela politica. Apaga da memoria deste
// modulo o que foi entregue antes da restricao (presenca nominal, estado da
// operacao, feed), so da operacao indicada. A propria linha de presenca fica.
// Nada e apagado no servidor; a volta ao acesso reenvia tudo pelo servidor.
export function purgeOperationData(operationId) {
    const op = sanitizeOperationId(operationId || currentOperationId);
    if (!op) return { purged: false };
    const { operatorId } = getLocalOperator();
    let removedParticipants = 0;
    Array.from(participants.entries()).forEach(([key, participant]) => {
        const participantOperation = participant?.operationId || currentOperationId;
        if (participantOperation !== op) return;
        if (operatorId && participant?.operatorId === operatorId) return;
        participants.delete(key);
        removedParticipants += 1;
    });
    let removedFeed = 0;
    for (let i = eventFeed.length - 1; i >= 0; i -= 1) {
        if (eventFeed[i]?.operationId === op) {
            eventFeed.splice(i, 1);
            removedFeed += 1;
        }
    }
    if (op === currentOperationId) {
        const keepStatus = operationState?.operationStatus;
        operationState = {
            ...DEFAULT_OPERATION_STATE,
            operationId: op,
            currentObjective: null,
            ...(keepStatus === 'CLOSED' ? { operationStatus: 'CLOSED' } : {}),
        };
        emitLocal('OPERATION_STATE_UPDATED', operationState);
    }
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
    return { purged: true, operationId: op, removedParticipants, removedFeed };
}

// Telao (F5): a aba de exibicao publica suspende o socket da sessao e o
// retoma ao sair. connect() e no-op com socket existente, por isso o
// caminho explicito.
export function resume() {
    if (socket && !connected) socket.connect();
}

export function getParticipants() {
    return Array.from(participants.values()).filter(isSameOperation);
}

export function isLiveOperation() {
    const activeParticipants = getParticipants();
    return connected && !degraded && activeParticipants.length > 0 && Boolean(currentOperationId);
}

export function isDegraded() { return degraded; }

export function sendOperationStateUpdate(patch = {}, operationId) {
    const op = sanitizeOperationId(operationId || currentOperationId);
    if (!op) {
        console.warn('[REALTIME] OPERATION_STATE_UPDATE ignorado: operationId ausente');
        operationState = { ...NO_OPERATION_STATE };
        emitLocal('OPERATION_STATE_UPDATED', operationState);
        emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
        return;
    }
    safeEmitEvent('OPERATION_STATE_UPDATE', { operationId: op, patch });
    const changedFields = Object.keys(patch).join(', ');
    pushFeed('OPERATION_STATE_UPDATE_REQUESTED', `Solicitada atualizacao do contexto operacional: ${changedFields}`, { operationId: op, patch });
}

// Sprint B (Secao 33 do Canon — "realtime e rebaixamento"): connect()
// vira no-op assim que ha um socket ativo (`if (connected || socket) return;`),
// entao currentIdentity so era atribuido uma vez, no handshake inicial —
// uma promocao/revogacao aplicada via ROLE_ASSIGNED atualizava o roster de
// outros participantes (linha ~528) mas nunca a propria identidade local,
// deixando getOperationSnapshot().role (consumido por
// OperationAwarenessBar.canUseCommandActions) desatualizado ate reconexao
// ou F5. updateIdentity() fecha esse gap: AuthContext chama isto logo
// apos re-buscar /api/auth/me em resposta a um ROLE_ASSIGNED que aponta
// para o proprio usuario.
export function updateIdentity(user) {
    if (!user) return;
    // user.operationalRole (Sprint A, escopado por operacao, resolvido por
    // /api/auth/me) tem precedencia sobre user.role (institucional) SOMENTE
    // quando representa uma atribuicao SCI real — nao o default Observer.
    // Sem essa ressalva, um Gestor da Operacao sem nenhuma OperationalAssignment
    // propria (o modo "Solo" documentado em operationalRoleService.js, que
    // age via userCanManageSimulations e nunca precisou de uma linha na
    // tabela) veria seu proprio snapshot.role cair para 'observer' e perderia
    // canUseCommandActions no proprio awareness bar. Mesma logica de
    // precedencia que updateOperatorRoleLive aplica em socket.data.role no
    // backend (socketServer.js) para papeis efetivamente atribuidos.
    const hasRealAssignment = user.operationalRole && user.operationalRole !== 'observer';
    const role = hasRealAssignment
        ? user.operationalRole
        : (user.role || user.roleName || user.operationalRole || null);
    currentIdentity = { ...(currentIdentity || {}), ...user, role };
    emitLocal('PRESENCE_UPDATED', getOperationSnapshot());
}

export function getOperationSnapshot() {
    const { role } = getLocalOperator();
    const list = getParticipants();
    const operationContext = currentOperationId ? (operationState || { ...DEFAULT_OPERATION_STATE, operationId: currentOperationId }) : { ...NO_OPERATION_STATE };
    let reconnectToken = null;
    try {
        reconnectToken = localStorage.getItem('reacta_reconnectToken') || null;
    } catch (error) {
        reconnectToken = null;
    }
    return {
        operationId: currentOperationId,
        status: list.length ? 'ACTIVE' : 'STANDBY',
        operatorCount: list.filter(item => item.online !== false).length,
        realtime: connected && !degraded ? 'REALTIME CONNECTED' : 'REALTIME UNAVAILABLE',
        connected,
        degraded,
        reconnecting,
        reconnectToken,
        latencyMs,
        lastEventAt,
        role,
        ready: currentReady,
        operationState: operationContext,
        participants: list,
        feed: eventFeed.filter(item => item.operationId === currentOperationId).slice(0, 12)
    };
}

export function getEventFeed() {
    return eventFeed.filter(item => item.operationId === currentOperationId);
}

export function getDebugSnapshot() {
    const snapshot = getOperationSnapshot();
    return {
        socketConnected: connected,
        socketId: socket?.id || null,
        room: currentOperationId,
        operationId: currentOperationId,
        reconnectToken: snapshot.reconnectToken,
        latencyMs,
        lastEventAt,
        degraded,
        reconnecting,
        realtime: snapshot.realtime,
        activeParticipants: snapshot.operatorCount,
        listenerCounts: Object.fromEntries(Object.entries(listeners).map(([key, value]) => [key, value.size || 0])),
        feedSize: eventFeed.length,
        participantSize: participants.size,
    };
}

export function emit(eventName, payload) {
    safeEmitEvent(eventName, payload);
}

// Decisoes que o operador precisa ver confirmadas (ex.: ADMIT_USER/DENY_USER):
// resolve com o ack do servidor; socket ausente ou sem resposta vira erro
// explicito em vez de envio descartado em silencio.
export function emitWithAck(eventName, payload, timeoutMs = 8000) {
    if (!socket || !connected) return Promise.resolve({ ok: false, error: 'realtime_unavailable' });
    return new Promise((resolve) => {
        socket.timeout(timeoutMs).emit(eventName, payload, (err, result) => {
            resolve(err ? { ok: false, error: 'realtime_timeout' } : (result || { ok: false, error: 'no_response' }));
        });
    });
}

export { sanitizeOperationId };

export default {
    disconnect: () => { clearInterval(heartbeatTimer); socket?.disconnect(); },
    resume,
    purgeOperationData,
    connect,
    updateIdentity,
    on,
    emit,
    emitWithAck,
    getActiveOperationId,
    subscribeOperationChange,
    verifyOperationConsistency,
    sendReady,
    sendOperationStateUpdate,
    joinOperation,
    leaveOperation,
    setOperationId,
    getParticipants,
    isDegraded,
    isLiveOperation,
    getOperationSnapshot,
    getEventFeed,
    getDebugSnapshot,
    sanitizeOperationId // Export the function here as well for direct access
};
